import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

export const fetchTbankIntegration = createAsyncThunk(
  'tbankIntegration/fetchTbankIntegration',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(`/companies/${companyId}/integrations/tbank`)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      if (err.response?.status === 404) {
        return { data: null, companyId: String(companyId) }
      }
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const fetchTbankStatus = createAsyncThunk(
  'tbankIntegration/fetchTbankStatus',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(`/companies/${companyId}/integrations/tbank/status`)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const createTbankIntegration = createAsyncThunk(
  'tbankIntegration/createTbankIntegration',
  async ({ companyId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.post(`/companies/${companyId}/integrations/tbank`, payload)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const updateTbankIntegration = createAsyncThunk(
  'tbankIntegration/updateTbankIntegration',
  async ({ companyId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.patch(`/companies/${companyId}/integrations/tbank`, payload)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const fetchReferralLinks = createAsyncThunk(
  'tbankIntegration/fetchReferralLinks',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(
        `/companies/${companyId}/integrations/tbank/referral-links`
      )
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const clickReferralLink = createAsyncThunk(
  'tbankIntegration/clickReferralLink',
  async ({ companyId, product } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!product) throw new Error('product is required')
      const response = await axiosClient.post(
        `/companies/${companyId}/integrations/tbank/referral-links/${product}/click`
      )
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  integration: null,
  status: 'idle',
  error: null,

  statusInfo: null,
  statusInfoStatus: 'idle',

  referralLinks: [],
  referralLinksStatus: 'idle',
  referralLinksError: null,

  saveStatus: 'idle',
  saveError: null,

  companyId: null,
}

export const slice = createSlice({
  name: 'tbankIntegration',
  initialState,

  reducers: {
    resetTbankIntegrationState(state) {
      state.integration = null
      state.status = 'idle'
      state.error = null
      state.companyId = null
    },
    resetTbankSaveState(state) {
      state.saveStatus = 'idle'
      state.saveError = null
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchTbankIntegration.pending, (state, action) => {
        state.status = 'loading'
        state.error = null
        state.companyId = String(action.meta.arg?.companyId ?? '')
      })
      .addCase(fetchTbankIntegration.fulfilled, (state, { payload }) => {
        state.status = 'succeeded'
        state.integration = payload?.data || null
      })
      .addCase(fetchTbankIntegration.rejected, (state, { payload }) => {
        state.status = 'failed'
        state.error = payload || 'Failed to fetch Tbank integration'
      })

      .addCase(fetchTbankStatus.pending, (state) => {
        state.statusInfoStatus = 'loading'
      })
      .addCase(fetchTbankStatus.fulfilled, (state, { payload }) => {
        state.statusInfoStatus = 'succeeded'
        state.statusInfo = payload?.data || null
      })
      .addCase(fetchTbankStatus.rejected, (state) => {
        state.statusInfoStatus = 'failed'
      })

      .addCase(createTbankIntegration.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(createTbankIntegration.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        state.integration = payload?.data || state.integration
      })
      .addCase(createTbankIntegration.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to connect Tbank'
      })

      .addCase(updateTbankIntegration.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(updateTbankIntegration.fulfilled, (state, { payload }) => {
        state.saveStatus = 'succeeded'
        state.integration = payload?.data || state.integration
      })
      .addCase(updateTbankIntegration.rejected, (state, { payload }) => {
        state.saveStatus = 'failed'
        state.saveError = payload || 'Failed to update Tbank integration'
      })

      .addCase(fetchReferralLinks.pending, (state) => {
        state.referralLinksStatus = 'loading'
        state.referralLinksError = null
      })
      .addCase(fetchReferralLinks.fulfilled, (state, { payload }) => {
        state.referralLinksStatus = 'succeeded'
        state.referralLinks = Array.isArray(payload?.data) ? payload.data : []
      })
      .addCase(fetchReferralLinks.rejected, (state, { payload }) => {
        state.referralLinksStatus = 'failed'
        state.referralLinksError = payload || 'Failed to fetch referral links'
      })

      .addCase(clickReferralLink.fulfilled, (state, { payload }) => {
        const updated = payload?.data
        if (!updated) return
        const idx = state.referralLinks.findIndex((x) => x?.product?.value === updated?.product?.value)
        if (idx >= 0) state.referralLinks[idx] = updated
      })
  },
})

export const { resetTbankIntegrationState, resetTbankSaveState } = slice.actions

export default slice.reducer
